import React, { useState, useEffect, useRef } from 'react';
import './App.css';

function App() {
  const [grossSalary, setGrossSalary] = useState(20000);
  const [isStudent, setIsStudent] = useState(false);
  const [isOver18, setIsOver18] = useState(true);
  const [isBornBefore1961, setIsBornBefore1961] = useState(false);
  const [maritalStatus, setMaritalStatus] = useState('single');
  
  // Expense tracking
  const [expenses, setExpenses] = useState([]);
  const [newExpense, setNewExpense] = useState({ name: '', amount: '' });
  const [remainingBudget, setRemainingBudget] = useState({ weekly: 0, monthly: 0, yearly: 0 });
  
  // References for scrolling
  const costOfLivingRef = useRef(null);
  
  const [breakdown, setBreakdown] = useState({
    weekly: {
      gross: 0,
      nationalInsurance: 0,
      tax: 0,
      cola: 0,
      net: 0
    },
    monthly: {
      gross: 0,
      nationalInsurance: 0,
      tax: 0,
      cola: 0,
      net: 0
    },
    yearly: {
      gross: 0,
      nationalInsurance: 0,
      tax: 0,
      cola: 0,
      net: 0
    }
  });

  useEffect(() => {
    calculateSalary();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grossSalary, isStudent, isOver18, isBornBefore1961, maritalStatus]);

  useEffect(() => {
    calculateRemainingBudget();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakdown, expenses]);

  const calculateSalary = () => {
    // Basic calculation (simplified for demo purposes)
    const yearlyGross = grossSalary;
    const monthlyGross = yearlyGross / 12;
    const weeklyGross = yearlyGross / 52;
    
    // Simplified tax calculation (for demonstration)
    const yearlyNI = yearlyGross * 0.1; // 10% for National Insurance
    const monthlyNI = yearlyNI / 12;
    const weeklyNI = yearlyNI / 52;
    
    let taxRate = 0.08; // default tax rate for single
    if (maritalStatus === 'married') {
      taxRate = 0.06; // reduced rate for married individuals
    } else if (maritalStatus === 'parent') {
      taxRate = 0.04; // further reduction for parents
    }

    const yearlyTax = yearlyGross * taxRate;
    const monthlyTax = yearlyTax / 12;
    const weeklyTax = yearlyTax / 52;
    
    const yearlyCola = yearlyGross * 0.0256; // 2.56% for COLA/Bonus
    const monthlyCola = yearlyCola / 12;
    const weeklyCola = yearlyCola / 52;
    
    const yearlyNet = yearlyGross - yearlyNI - yearlyTax;
    const monthlyNet = monthlyGross - monthlyNI - monthlyTax;
    const weeklyNet = weeklyGross - weeklyNI - weeklyTax;
    
    setBreakdown({
      weekly: {
        gross: weeklyGross.toFixed(2),
        nationalInsurance: weeklyNI.toFixed(2),
        tax: weeklyTax.toFixed(2),
        cola: weeklyCola.toFixed(2),
        net: weeklyNet.toFixed(2)
      },
      monthly: {
        gross: monthlyGross.toFixed(2),
        nationalInsurance: monthlyNI.toFixed(2),
        tax: monthlyTax.toFixed(2),
        cola: monthlyCola.toFixed(2),
        net: monthlyNet.toFixed(2)
      },
      yearly: {
        gross: yearlyGross.toFixed(2),
        nationalInsurance: yearlyNI.toFixed(2),
        tax: yearlyTax.toFixed(2),
        cola: yearlyCola.toFixed(2),
        net: yearlyNet.toFixed(2)
      }
    });
  };

  const calculateRemainingBudget = () => {
    const totalMonthlyExpenses = expenses.reduce((total, expense) => total + Number(expense.amount), 0);
    const totalYearlyExpenses = totalMonthlyExpenses * 12;
    const totalWeeklyExpenses = totalYearlyExpenses / 52;

    setRemainingBudget({
      weekly: (Number(breakdown.weekly.net) - totalWeeklyExpenses).toFixed(2),
      monthly: (Number(breakdown.monthly.net) - totalMonthlyExpenses).toFixed(2),
      yearly: (Number(breakdown.yearly.net) - totalYearlyExpenses).toFixed(2)
    });
  };

  const handleSliderChange = (e) => {
    setGrossSalary(Number(e.target.value));
  };

  const handleExpenseInputChange = (e) => {
    const { name, value } = e.target;
    setNewExpense(prev => ({ ...prev, [name]: value }));
  };

  const addExpense = () => {
    if (newExpense.name.trim() === '' || !newExpense.amount) return;
    
    setExpenses([...expenses, { 
      id: Date.now(), 
      name: newExpense.name, 
      amount: Number(newExpense.amount) 
    }]);
    
    setNewExpense({ name: '', amount: '' });
  };

  const removeExpense = (id) => {
    setExpenses(expenses.filter(expense => expense.id !== id));
  };

  const scrollToSection = (ref) => {
    ref.current.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="calculator-container">
      <div className="calculator-header">
        <div className="logo">
          <div className="logo-icon">
            <div className="abacus-icon">
              <div className="abacus-frame"></div>
              <div className="abacus-rod"></div>
              <div className="abacus-rod"></div>
              <div className="abacus-rod"></div>
              <div className="abacus-bead"></div>
              <div className="abacus-bead"></div>
              <div className="abacus-bead"></div>
              <div className="abacus-bead"></div>
              <div className="abacus-bead"></div>
              <div className="abacus-bead"></div>
            </div>
          </div>
          <div className="logo-text">
            <h1>Malta Salary and Budget Calculator</h1>
          </div>
        </div>
      </div>
      
      <div className="calculator-main">
        <div className="input-section">
          <h2>Enter Your Yearly Gross Salary</h2>
          <div className="salary-input">
            <span className="currency">€</span>
            <input 
              type="number" 
              value={grossSalary} 
              onChange={(e) => setGrossSalary(Number(e.target.value))}
            />
          </div>
          <div className="slider-container">
            <input 
              type="range" 
              min="10000" 
              max="100000" 
              step="500" 
              value={grossSalary} 
              onChange={handleSliderChange}
              className="slider"
            />
          </div>
          <div className="options">
            <div className="option-row">
              <label>
                <input
                  type="checkbox"
                  checked={isStudent}
                  onChange={() => setIsStudent(!isStudent)}
                />
                Are you a student?
              </label>
            </div>
            <div className="option-row">
              <label>
                <input
                  type="checkbox"
                  checked={isOver18}
                  onChange={() => setIsOver18(!isOver18)}
                />
                Are you over 18 years old?
              </label>
            </div>
            <div className="option-row">
              <label>
                <input
                  type="checkbox"
                  checked={isBornBefore1961}
                  onChange={() => setIsBornBefore1961(!isBornBefore1961)}
                />
                Were you born before 31st December 1961?
              </label>
            </div>
            <div className="option-row status-options">
              <label>
                <input
                  type="radio"
                  name="maritalStatus"
                  value="single"
                  checked={maritalStatus === 'single'}
                  onChange={() => setMaritalStatus('single')}
                />
                Single
              </label>
              <label>
                <input
                  type="radio"
                  name="maritalStatus"
                  value="married"
                  checked={maritalStatus === 'married'}
                  onChange={() => setMaritalStatus('married')}
                />
                Married
              </label>
              <label>
                <input
                  type="radio"
                  name="maritalStatus"
                  value="parent"
                  checked={maritalStatus === 'parent'}
                  onChange={() => setMaritalStatus('parent')}
                />
                Parent
              </label>
            </div>
          </div>
        </div>
        
        <div className="results-section">
          <h2>Gross Income Breakdown</h2>
          <div className="breakdown-table">
            <div className="table-header">
              <div className="header-cell"></div>
              <div className="header-cell">Weekly</div>
              <div className="header-cell">Monthly</div>
              <div className="header-cell">Yearly</div>
            </div>
            
            <div className="table-row">
              <div className="row-label">Gross Salary</div>
              <div className="row-value"><span className="currency-symbol">€</span>{breakdown.weekly.gross}</div>
              <div className="row-value"><span className="currency-symbol">€</span>{breakdown.monthly.gross}</div>
              <div className="row-value"><span className="currency-symbol">€</span>{breakdown.yearly.gross}</div>
            </div>
            
            <div className="table-row">
              <div className="row-label">Insurance</div>
              <div className="row-value"><span className="currency-symbol">€</span>{breakdown.weekly.nationalInsurance}</div>
              <div className="row-value"><span className="currency-symbol">€</span>{breakdown.monthly.nationalInsurance} <span className="note">*</span></div>
              <div className="row-value"><span className="currency-symbol">€</span>{breakdown.yearly.nationalInsurance}</div>
            </div>
            
            <div className="table-row">
              <div className="row-label">Tax</div>
              <div className="row-value"><span className="currency-symbol">€</span>{breakdown.weekly.tax}</div>
              <div className="row-value"><span className="currency-symbol">€</span>{breakdown.monthly.tax}</div>
              <div className="row-value"><span className="currency-symbol">€</span>{breakdown.yearly.tax}</div>
            </div>
            
            <div className="table-row">
              <div className="row-label">Bonus</div>
              <div className="row-value"><span className="currency-symbol">€</span>{breakdown.weekly.cola} <span className="note">**</span></div>
              <div className="row-value"><span className="currency-symbol">€</span>{breakdown.monthly.cola} <span className="note">**</span></div>
              <div className="row-value"><span className="currency-symbol">€</span>{breakdown.yearly.cola} <span className="note">**</span></div>
            </div>
            
            <div className="table-row total-row">
              <div className="row-label">Take-Home Pay</div>
              <div className="row-value"><span className="currency-symbol">€</span>{breakdown.weekly.net}</div>
              <div className="row-value"><span className="currency-symbol">€</span>{breakdown.monthly.net}</div>
              <div className="row-value"><span className="currency-symbol">€</span>{breakdown.yearly.net}</div>
            </div>
          </div>
          <div className="footnotes">
            <p>* Changes based on weeks in month</p>
            <p>** Not included in take-home pay</p>
          </div>
        </div>
      </div>
      
      <div className="budget-section">
        <div className="budget-header">
          <h2>Track Your Spending</h2>
          <button 
            className="reference-link"
            onClick={() => scrollToSection(costOfLivingRef)}
          >
            See Costs in Malta
          </button>
        </div>
        <div className="budget-container">
          <div className="expense-input-section">
            <h3>Add Monthly Costs</h3>
            <div className="expense-form">
              <div className="input-group">
                <input 
                  type="text" 
                  placeholder="Cost name"
                  name="name"
                  value={newExpense.name}
                  onChange={handleExpenseInputChange}
                />
              </div>
              <div className="input-group">
                <span className="currency-small">€</span>
                <input 
                  type="number" 
                  placeholder="Amount"
                  name="amount"
                  value={newExpense.amount}
                  onChange={handleExpenseInputChange}
                />
              </div>
              <button className="add-expense-btn" onClick={addExpense}>
                Add
              </button>
            </div>
            
            <div className="expense-list">
              <h3>Your Costs</h3>
              {expenses.length === 0 ? (
                <p className="no-expenses">No costs added yet.</p>
              ) : (
                <ul>
                  {expenses.map(expense => (
                    <li key={expense.id} className="expense-item">
                      <div className="expense-info">
                        <span className="expense-name">{expense.name}</span>
                        <span className="expense-amount">€{expense.amount.toFixed(2)}</span>
                      </div>
                      <button 
                        className="remove-expense-btn" 
                        onClick={() => removeExpense(expense.id)}
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              
              <div className="expense-total">
                <span>Total Costs:</span>
                <span>€{expenses.reduce((sum, expense) => sum + expense.amount, 0).toFixed(2)}/month</span>
              </div>
            </div>
          </div>
          
          <div className="budget-summary-section">
            <h3>Money Left After Costs</h3>
            <div className="remaining-budget-table">
              <div className="table-header">
                <div className="header-cell">Weekly</div>
                <div className="header-cell">Monthly</div>
                <div className="header-cell">Yearly</div>
              </div>
              <div className="table-row total-row">
                <div className="row-value">€{remainingBudget.weekly}</div>
                <div className="row-value">€{remainingBudget.monthly}</div>
                <div className="row-value">€{remainingBudget.yearly}</div>
              </div>
            </div>
            
            <div className="budget-chart">
              <div className="budget-breakdown">
                <div className="chart-label">Income vs. Costs</div>
                <div className="chart-bars">
                  <div className="chart-bar">
                    <div className="bar-label">Money In</div>
                    <div className="bar income-bar" style={{ height: '100%' }}>
                      <span className="bar-value">€{breakdown.monthly.net}</span>
                    </div>
                  </div>
                  <div className="chart-bar">
                    <div className="bar-label">Money Out</div>
                    <div 
                      className="bar expense-bar" 
                      style={{ 
                        height: `${Math.min(100, expenses.reduce((sum, expense) => sum + expense.amount, 0) / Number(breakdown.monthly.net) * 100)}%` 
                      }}
                    >
                      <span className="bar-value">
                        €{expenses.reduce((sum, expense) => sum + expense.amount, 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div className="chart-bar">
                    <div className="bar-label">Left Over</div>
                    <div 
                      className="bar remaining-bar"
                      style={{ 
                        height: `${Math.min(100, Number(remainingBudget.monthly) / Number(breakdown.monthly.net) * 100)}%` 
                      }}
                    >
                      <span className="bar-value">
                        €{remainingBudget.monthly}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="cost-of-living-section" ref={costOfLivingRef}>
        <h2>Malta Cost of Living Reference</h2>
        <div className="reference-content">
          <div className="reference-category">
            <div className="category-icon">🍽️</div>
            <h3>Restaurants</h3>
            <div className="category-items">
              <div className="reference-item">
                <div className="item-name">Meal, Inexpensive Restaurant</div>
                <div className="item-price">€15.00</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Meal for 2 People, Mid-range Restaurant, Three-course</div>
                <div className="item-price">€70.08</div>
              </div>
              <div className="reference-item">
                <div className="item-name">McMeal at McDonalds (or Equivalent Combo Meal)</div>
                <div className="item-price">€10.00</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Domestic Beer (0.5 liter draught)</div>
                <div className="item-price">€3.50</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Imported Beer (0.33 liter bottle)</div>
                <div className="item-price">€4.00</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Cappuccino (regular)</div>
                <div className="item-price">€2.41</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Coke/Pepsi (0.33 liter bottle)</div>
                <div className="item-price">€2.15</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Water (0.33 liter bottle)</div>
                <div className="item-price">€1.42</div>
              </div>
            </div>
          </div>

          <div className="reference-category">
            <div className="category-icon">🛒</div>
            <h3>Markets</h3>
            <div className="category-items">
              <div className="reference-item">
                <div className="item-name">Milk (regular), (1 liter)</div>
                <div className="item-price">€1.14</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Loaf of Fresh White Bread (500g)</div>
                <div className="item-price">€1.15</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Rice (white), (1kg)</div>
                <div className="item-price">€3.10</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Eggs (regular) (12)</div>
                <div className="item-price">€3.18</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Local Cheese (1kg)</div>
                <div className="item-price">€9.24</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Chicken Fillets (1kg)</div>
                <div className="item-price">€8.27</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Beef Round (1kg) (or Equivalent Back Leg Red Meat)</div>
                <div className="item-price">€14.73</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Apples (1kg)</div>
                <div className="item-price">€2.73</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Banana (1kg)</div>
                <div className="item-price">€1.81</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Oranges (1kg)</div>
                <div className="item-price">€2.36</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Tomato (1kg)</div>
                <div className="item-price">€2.85</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Potato (1kg)</div>
                <div className="item-price">€1.72</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Onion (1kg)</div>
                <div className="item-price">€1.79</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Lettuce (1 head)</div>
                <div className="item-price">€1.64</div>
              </div>
            </div>
          </div>

          <div className="reference-category">
            <div className="category-icon">🚗</div>
            <h3>Transportation</h3>
            <div className="category-items">
              <div className="reference-item">
                <div className="item-name">One-way Ticket (Local Transport)</div>
                <div className="item-price">€2.00</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Monthly Pass (Regular Price)</div>
                <div className="item-price">€0.00</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Taxi Start (Normal Tariff)</div>
                <div className="item-price">€5.00</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Taxi 1km (Normal Tariff)</div>
                <div className="item-price">€2.00</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Taxi 1hour Waiting (Normal Tariff)</div>
                <div className="item-price">€37.50</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Gasoline (1 liter)</div>
                <div className="item-price">€1.34</div>
              </div>
            </div>
          </div>

          <div className="reference-category">
            <div className="category-icon">🏠</div>
            <h3>Utilities (Monthly)</h3>
            <div className="category-items">
              <div className="reference-item">
                <div className="item-name">Basic (Electricity, Heating, Cooling, Water, Garbage) for 85m2 Apartment</div>
                <div className="item-price">€90.18</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Mobile Phone Monthly Plan with Calls and 10GB+ Data</div>
                <div className="item-price">€23.95</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Internet (60 Mbps or More, Unlimited Data, Cable/ADSL)</div>
                <div className="item-price">€30.91</div>
              </div>
            </div>
          </div>

          <div className="reference-category">
            <div className="category-icon">🏃</div>
            <h3>Sports And Leisure</h3>
            <div className="category-items">
              <div className="reference-item">
                <div className="item-name">Fitness Club, Monthly Fee for 1 Adult</div>
                <div className="item-price">€57.25</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Tennis Court Rent (1 Hour on Weekend)</div>
                <div className="item-price">€21.03</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Cinema, International Release, 1 Seat</div>
                <div className="item-price">€9.30</div>
              </div>
            </div>
          </div>

          <div className="reference-category">
            <div className="category-icon">👶</div>
            <h3>Childcare</h3>
            <div className="category-items">
              <div className="reference-item">
                <div className="item-name">Preschool (or Kindergarten), Full Day, Private, Monthly for 1 Child</div>
                <div className="item-price">€474.60</div>
              </div>
              <div className="reference-item">
                <div className="item-name">International Primary School, Yearly for 1 Child</div>
                <div className="item-price">€9,200.42</div>
              </div>
            </div>
          </div>

          <div className="reference-category">
            <div className="category-icon">👕</div>
            <h3>Clothing And Shoes</h3>
            <div className="category-items">
              <div className="reference-item">
                <div className="item-name">1 Pair of Jeans (Levis 501 Or Similar)</div>
                <div className="item-price">€88.71</div>
              </div>
              <div className="reference-item">
                <div className="item-name">1 Summer Dress in a Chain Store (Zara, H&M, ...)</div>
                <div className="item-price">€40.11</div>
              </div>
              <div className="reference-item">
                <div className="item-name">1 Pair of Nike Running Shoes (Mid-Range)</div>
                <div className="item-price">€87.93</div>
              </div>
              <div className="reference-item">
                <div className="item-name">1 Pair of Men Leather Business Shoes</div>
                <div className="item-price">€98.17</div>
              </div>
            </div>
          </div>

          <div className="reference-category">
            <div className="category-icon">🏢</div>
            <h3>Rent Per Month</h3>
            <div className="category-items">
              <div className="reference-item">
                <div className="item-name">Apartment (1 bedroom) in City Centre</div>
                <div className="item-price">€978.46</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Apartment (1 bedroom) Outside of Centre</div>
                <div className="item-price">€813.97</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Apartment (3 bedrooms) in City Centre</div>
                <div className="item-price">€1,654.55</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Apartment (3 bedrooms) Outside of Centre</div>
                <div className="item-price">€1,279.88</div>
              </div>
            </div>
          </div>

          <div className="reference-category">
            <div className="category-icon">🏘️</div>
            <h3>Buy Apartment Price</h3>
            <div className="category-items">
              <div className="reference-item">
                <div className="item-name">Price per Square Meter to Buy Apartment in City Centre</div>
                <div className="item-price">€3,581.48</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Price per Square Meter to Buy Apartment Outside of Centre</div>
                <div className="item-price">€2,815.32</div>
              </div>
            </div>
          </div>

          <div className="reference-category">
            <div className="category-icon">💰</div>
            <h3>Salaries And Financing</h3>
            <div className="category-items">
              <div className="reference-item">
                <div className="item-name">Average Monthly Net Salary (After Tax)</div>
                <div className="item-price">€1,508.13</div>
              </div>
              <div className="reference-item">
                <div className="item-name">Mortgage Interest Rate in Percentages (%), Yearly, for 20 Years Fixed-Rate</div>
                <div className="item-price">4.70%</div>
              </div>
            </div>
          </div>
        </div>
        <div className="data-source">
          <p>Data source: <a href="https://www.numbeo.com/" target="_blank" rel="noopener noreferrer">Numbeo.com</a></p>
          <p className="updated-info">Updated for 2025</p>
        </div>
      </div>
    </div>
  );
}

export default App;
